import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClients, useContracts, useConvenios } from '../contexts/DataContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Input } from '../components/input';
import { Button } from '../components/button';
import SearchableListbox from '../components/SearchableListbox';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../components/table';
import { ContratoView } from '../components/ContratoView';
import { FianzaView } from '../components/FianzaView';
import AssignedUsersBubbles from '../components/AssignedUsersBubbles';
import UserAssignmentSelector from '../components/UserAssignmentSelector';
import { Dialog, DialogTitle, DialogBody } from '../components/dialog';
import { useData } from '../contexts/DataContext';
import { getPrimaAmount, getFacturasVigentes } from '../utils/calculations';
import { getFianzaStatus, computePendingFianzas, getFianzaPorcentaje } from '../utils/convenioCompliance';
import ContratoFianzasPanel from '../components/convenio/ContratoFianzasPanel';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../hooks/useFrozenColumns';
import {
  ensureFacturas,
  getEndososAbiertos,
  clasificarEndosoPendiente,
  ESTADO_FIANZA,
} from '../utils/fianzaEventos';
import { PencilSquareIcon, ArrowUpIcon, ArrowDownIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useUser } from '../components/UserContext';
import { logBitacora, computeUserAssignmentChanges } from '../utils/bitacora';

const URGENCIA_ORDER = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const fmtMXN = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

const Contratos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const syncRef = useRef(false);
  const paramsHydratedRef = useRef(false);
  const hydrationTargetRef = useRef(null);
  const resetGuardRef = useRef(true);
  const { clientsList, clientsById, clientsLoading } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { updateContractAssignedUsers } = useData();
  const { conveniosById } = useConvenios();
  const { user, userData, getUserName } = useUser();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(7);
  const [selectedClient, setSelectedClient] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);
  const [selectedContratoForAssignment, setSelectedContratoForAssignment] = useState(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [filterFianzas, setFilterFianzas] = useState('all');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Freeze panes: the chevron and Cliente columns stay put while the rest
  // scrolls sideways.
  const frozen = useFrozenColumns(2);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);

  const clientsData = useMemo(() => clientsById, [clientsById]);
  const clientNames = useMemo(() => {
    const names = {};
    clientsList.forEach((cliente) => {
      names[cliente.id] = cliente.nombreEmpresa || "Sin nombre";
    });
    return names;
  }, [clientsList]);

  // Per-contract fianza rollup: lifecycle counts, montos, endosos pendientes,
  // and the per-fianza rows the expandable panel renders. Terminal fianzas
  // count as "terminadas" and stay out of the monto/prima totals.
  const rollups = useMemo(() => {
    const now = Date.now();
    const map = {};
    contractsList.forEach((contrato) => {
      const fianzas = contrato.fianzas || [];
      const r = {
        emitidas: fianzas.length,
        vigentes: 0,
        porVencer: 0,
        vencidas: 0,
        porIniciar: 0,
        terminadas: 0,
        montoAfianzado: 0,
        primaVigente: 0,
        endososPendientes: 0,
        endososUrgencia: null,
        // The requirements themselves, enriched with dueDate/isOverdue/isFutura —
        // the expandable panel lists them, so a count alone isn't enough.
        pendientes: computePendingFianzas([contrato]),
        documentos: (contrato.documentos || []).length,
        rows: [],
      };
      r.reqPendientes = r.pendientes.length;
      fianzas.forEach((fianza) => {
        const status = getFianzaStatus(fianza, now);
        const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
        const prima = getPrimaAmount(fianza) || 0;

        const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
        const pagadas = facturasVigentes.filter(f => f.pagada).length;
        const pagoEstado =
          facturasVigentes.length > 0 && pagadas === facturasVigentes.length
            ? 'pagada'
            : pagadas > 0
            ? 'parcial'
            : 'pendiente';

        const abiertos = status.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(fianza) : [];
        abiertos.forEach(p => {
          const u = clasificarEndosoPendiente(p, now).code;
          if (!r.endososUrgencia || URGENCIA_ORDER[u] < URGENCIA_ORDER[r.endososUrgencia]) {
            r.endososUrgencia = u;
          }
        });
        r.endososPendientes += abiertos.length;

        if (status.estado === ESTADO_FIANZA.ACTIVA) {
          r.montoAfianzado += Number(fianza.montoAfianzado) || 0;
          if (status.code === 'vigente') {
            r.vigentes += 1;
            r.primaVigente += prima;
            if (fin > 0 && fin - now <= THIRTY_DAYS) r.porVencer += 1;
          } else if (status.code === 'vencida') {
            r.vencidas += 1;
          } else {
            r.porIniciar += 1;
          }
        } else {
          r.terminadas += 1;
        }

        r.rows.push({
          fianza: { ...fianza, porcentajeInfo: getFianzaPorcentaje(fianza, contrato) },
          status,
          prima,
          pagoEstado,
          pagadas,
          facturasTotal: facturasVigentes.length,
          endososPendientes: abiertos.length,
        });
      });
      map[contrato.id] = r;
    });
    return map;
  }, [contractsList]);

  // Helper to get display name for a contract's parent (client or convenio)
  const getContractParentName = (contrato) => {
    if (contrato.convenioId) {
      const convenio = conveniosById[contrato.convenioId];
      const beneficiario = (convenio?.beneficiarios || []).find(
        (b) => b.id === contrato.beneficiarioId
      );
      const benefName = beneficiario?.nombre || '';
      return `${convenio?.nombre || 'Convenio'} ${benefName ? `- ${benefName}` : ''}`;
    }
    return clientNames[contrato.clientId] || 'Cargando...';
  };

  // Sort contracts based on selected sort option
  const contratos = useMemo(() => {
    return [...contractsList].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'createdAt':
          comparison = (a.createdAt?.toMillis?.() || a.createdAt || 0) - (b.createdAt?.toMillis?.() || b.createdAt || 0);
          break;
        case 'monto':
          comparison = (a.montoContrato?.value || 0) - (b.montoContrato?.value || 0);
          break;
        case 'cliente':
          const clientNameA = clientNames[a.clientId] || '';
          const clientNameB = clientNames[b.clientId] || '';
          comparison = clientNameA.localeCompare(clientNameB);
          break;
        case 'titulo':
          comparison = (a.tituloContrato?.value || '').localeCompare(b.tituloContrato?.value || '');
          break;
        case 'numeroContrato':
          comparison = (a.numeroContrato?.value || '').localeCompare(b.numeroContrato?.value || '');
          break;
        case 'fianzasRequeridas':
          comparison = (a.fianzasRequeridas?.value?.length || 0) - (b.fianzasRequeridas?.value?.length || 0);
          break;
        case 'fianzasEmitidas':
          comparison = (a.fianzas?.length || 0) - (b.fianzas?.length || 0);
          break;
        default:
          comparison = (a.createdAt?.toMillis?.() || a.createdAt || 0) - (b.createdAt?.toMillis?.() || b.createdAt || 0);
      }

      // Apply sort order
      comparison = sortOrder === 'asc' ? comparison : -comparison;

      // Secondary sort by ID for stable ordering when primary is equal
      if (comparison === 0) {
        return (a.id || '').localeCompare(b.id || '');
      }

      return comparison;
    });
  }, [contractsList, sortBy, sortOrder, clientNames]);
  const loading = clientsLoading || contractsLoading;

  // Reset to first page when filters change (skip initial render and wait for params hydration)
  useEffect(() => {
    if (resetGuardRef.current) {
      resetGuardRef.current = false;
      return;
    }
    if (!paramsHydratedRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, selectedClient, sortBy, sortOrder, filterFianzas]);

  // Seed state from URL params (page/perPage) and keep in sync without loops
  useEffect(() => {
    if (syncRef.current) {
      syncRef.current = false;
      return;
    }
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    const perPageParam = parseInt(params.get('perPage') || `${itemsPerPage}`, 10);

    hydrationTargetRef.current = {
      page: !Number.isNaN(pageParam) && pageParam > 0 ? pageParam : 1,
      perPage: !Number.isNaN(perPageParam) && perPageParam > 0 ? perPageParam : itemsPerPage,
      done: false,
    };
    if (!Number.isNaN(pageParam) && pageParam > 0) {
      setCurrentPage(pageParam);
    }
    if (!Number.isNaN(perPageParam) && perPageParam > 0) {
      setItemsPerPage(perPageParam);
    }
    paramsHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Mark hydration done when state matches seeded values
  useEffect(() => {
    const target = hydrationTargetRef.current;
    if (!target || target.done === true) return;
    if (currentPage === target.page && itemsPerPage === target.perPage) {
      hydrationTargetRef.current = { ...target, done: true };
    }
  }, [currentPage, itemsPerPage]);

  // Keep URL in sync with current page/perPage for shareable deep-links
  useEffect(() => {
    if (!paramsHydratedRef.current) return;
    const target = hydrationTargetRef.current;
    if (target && target.done === false) return;
    const params = new URLSearchParams(location.search);
    params.set('page', currentPage);
    params.set('perPage', itemsPerPage);
    const newSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();
    if (newSearch !== currentSearch) {
      syncRef.current = true;
      navigate({ pathname: '/app/contratos', search: newSearch }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, location.search]);

  const getFilteredContratos = () => {
    let filtered = contratos;

    if (selectedClient !== 'all') {
      filtered = filtered.filter(contrato => contrato.clientId === selectedClient);
    }

    if (filterFianzas === 'pendientes') {
      filtered = filtered.filter(contrato =>
        contrato.fianzasRequeridas?.value?.some(fr => !fr.fianzaId)
      );
    } else if (filterFianzas === 'endosos') {
      filtered = filtered.filter(contrato => (rollups[contrato.id]?.endososPendientes || 0) > 0);
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(contrato => {
        const clientName = clientNames[contrato.clientId]?.toLowerCase() || '';
        const parentName = getContractParentName(contrato).toLowerCase();
        const contratoTitle = contrato.tituloContrato?.value?.toLowerCase() || '';
        const contratante = contrato.contratante?.value?.toLowerCase() || '';
        const numeroContrato = contrato.numeroContrato?.value?.toLowerCase() || '';

        return clientName.includes(searchLower) ||
               parentName.includes(searchLower) ||
               contratoTitle.includes(searchLower) ||
               contratante.includes(searchLower) ||
               numeroContrato.includes(searchLower);
      });
    }

    return filtered;
  };

  const filteredContratos = getFilteredContratos();

  // Clamp page if data shrinks
  useEffect(() => {
    if (loading) return;
    const target = hydrationTargetRef.current;
    if (target && target.done === false) return;
    const total = filteredContratos.length;
    const maxPage = Math.max(1, Math.ceil(total / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredContratos.length, itemsPerPage, currentPage, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(filteredContratos.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContratos = filteredContratos.slice(startIndex, endIndex);

  const handleContratoClick = (contrato) => {
    setSelectedContrato(contrato);
    setIsContratoViewOpen(true);
  };
  const handleContratoViewClose = () => {
    setIsContratoViewOpen(false);
    setSelectedContrato(null);
  };

  const toggleExpanded = (contratoId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  };

  const openFianza = (contrato, fianza) => {
    setSelectedFianza({ ...fianza, contratoId: contrato.id, clientId: contrato.clientId });
    setIsFianzaViewOpen(true);
  };

  const FianzaBadges = ({ rollup }) => {
    if (rollup.emitidas === 0 && rollup.reqPendientes === 0) {
      return <Text className="text-sm text-zinc-400">Sin fianzas</Text>;
    }
    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1">
          {rollup.vigentes > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
              {rollup.vigentes} vig.
            </span>
          )}
          {rollup.porVencer > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
              {rollup.porVencer} por vencer
            </span>
          )}
          {rollup.vencidas > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
              {rollup.vencidas} venc.
            </span>
          )}
          {rollup.porIniciar > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
              {rollup.porIniciar} por iniciar
            </span>
          )}
          {rollup.terminadas > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">
              {rollup.terminadas} term.
            </span>
          )}
        </div>
        {rollup.reqPendientes > 0 && (
          <Text className="text-xs font-medium text-orange-600">
            {rollup.reqPendientes} por emitir
          </Text>
        )}
        {rollup.endososPendientes > 0 && (
          <span className={`flex items-center gap-1 text-xs font-medium ${
            rollup.endososUrgencia === 'vencido' || rollup.endososUrgencia === 'urgente'
              ? 'text-red-600'
              : 'text-amber-600'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              rollup.endososUrgencia === 'vencido' || rollup.endososUrgencia === 'urgente'
                ? 'bg-red-500'
                : 'bg-amber-500'
            }`}></span>
            {rollup.endososPendientes === 1
              ? 'Endoso pendiente'
              : `${rollup.endososPendientes} endosos pendientes`}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-4">
        <Heading level={1} className="text-azul-surelia mb-1">Contratos</Heading>
        <Text className="text-zinc-600">Visualiza y gestiona todos los contratos</Text>
      </div>

      {/* Filters */}
      <div className="mb-4 bg-white rounded-lg shadow-sm border border-zinc-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          {/* Client Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Cliente:</label>
            <SearchableListbox
              value={selectedClient}
              onChange={setSelectedClient}
              options={[
                { value: 'all', label: 'Todos los clientes' },
                ...Object.entries(clientNames)
                  .sort(([, a], [, b]) => a.localeCompare(b))
                  .map(([id, name]) => ({ value: id, label: name })),
              ]}
              className="min-w-[240px] max-w-[260px]"
              placeholder="Selecciona cliente"
              searchPlaceholder="Buscar cliente..."
              popupClassName="w-[260px]"
            />
          </div>

          {/* Fianzas filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Fianzas:</label>
            <select
              value={filterFianzas}
              onChange={(e) => setFilterFianzas(e.target.value)}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[200px]"
            >
              <option value="all">Todos</option>
              <option value="pendientes">Con requeridas pendientes</option>
              <option value="endosos">Con endosos pendientes</option>
            </select>
          </div>

          {/* Items per page */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Mostrar:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[60px]"
            >
              <option value={7}>7</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* Sort controls */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Ordenar:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm min-w-[140px]"
            >
              <option value="createdAt">Fecha de creación</option>
              <option value="monto">Monto</option>
              <option value="cliente">Cliente</option>
              <option value="titulo">Título</option>
              <option value="numeroContrato">No. Contrato</option>
              <option value="fianzasRequeridas">Fianzas requeridas</option>
              <option value="fianzasEmitidas">Fianzas emitidas</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 rounded-lg border border-azul-surelia/30 text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
              title={sortOrder === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              {sortOrder === 'asc' ? (
                <ArrowUpIcon className="w-4 h-4" />
              ) : (
                <ArrowDownIcon className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Search Input */}
          <Input
            type="text"
            placeholder="Buscar por cliente, contrato..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] sm:max-w-xs"
          />
        </div>
      </div>

      {/* Contratos table */}
      <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
        <div className="relative">
          <Table onScroll={frozen.onScroll}>
            <TableHead className="bg-azul-surelia">
              <TableRow>
                <TableHeader
                  ref={frozen.setRef(0)}
                  style={{ left: frozen.offsets[0] }}
                  className={`text-white font-semibold py-4 border-b-0 w-8 ${frozenHeadClass}`}
                ></TableHeader>
                <TableHeader
                  ref={frozen.setRef(1)}
                  style={{ left: frozen.offsets[1] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >No. Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Responsables</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Fianzas</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Monto Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzado / Prima</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Título del Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Beneficiario</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedContratos.map((contrato, index) => {
                const rollup = rollups[contrato.id] || { rows: [], emitidas: 0, reqPendientes: 0, documentos: 0 };
                const expanded = expandedIds.has(contrato.id);
                return (
                  <React.Fragment key={contrato.id}>
                    <TableRow
                      className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                      }`}
                      onClick={() => handleContratoClick(contrato)}
                    >
                      <TableCell
                        style={{ left: frozen.offsets[0] }}
                        className={`py-3 pr-0 ${frozenCellClass(index)}`}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(contrato.id);
                          }}
                          className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                          title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                        >
                          {expanded ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell
                        style={{ left: frozen.offsets[1] }}
                        className={`py-3 ${frozenCellClass(index)}`}
                      >
                        <div>
                          {contrato.numeroContrato?.value ? (
                            <Text
                              className="text-sm font-mono font-medium text-zinc-800"
                              title={contrato.numeroContrato.value}
                            >
                              {contrato.numeroContrato.value}
                            </Text>
                          ) : (
                            <Text className="text-sm text-zinc-400">Sin número</Text>
                          )}
                          {/* El cliente pasa a segunda línea, pero conserva su
                              navegación al cliente o al convenio. */}
                          <button
                            className="block text-xs text-azul-surelia/70 hover:text-azul-surelia hover:underline"
                            title={getContractParentName(contrato)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (contrato.convenioId) {
                                navigate(`/app/convenios/${contrato.convenioId}`);
                              } else {
                                const params = new URLSearchParams({
                                  page: currentPage.toString(),
                                  perPage: itemsPerPage.toString(),
                                });
                                navigate(`/app/clientes/${contrato.clientId}?${params.toString()}`, {
                                  state: {
                                    fromContratosFianzas: true,
                                    targetPath: '/app/contratos',
                                    page: currentPage,
                                    perPage: itemsPerPage,
                                  },
                                });
                              }
                            }}
                          >
                            {getContractParentName(contrato)}
                          </button>
                        </div>
                      </TableCell>

                      <TableCell className="py-3">
                        <div className="flex items-center gap-2">
                          <AssignedUsersBubbles
                            userIds={[
                              ...(clientsData[contrato.clientId]?.assignedUsers || []),
                              ...(contrato.assignedUsers || [])
                            ].filter((v, i, a) => a.indexOf(v) === i)}
                            maxDisplay={3}
                            size="sm"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedContratoForAssignment(contrato);
                              setIsAssignmentDialogOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title="Editar responsables"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>

                      <TableCell className="py-3">
                        <FianzaBadges rollup={rollup} />
                      </TableCell>

                      <TableCell className="py-3">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                          contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                          contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {contrato.estatus?.value || 'N/A'}
                        </span>
                      </TableCell>

                      <TableCell className="py-3">
                        <Text className="text-sm font-semibold text-azul-surelia">
                          {fmtMXN(contrato.montoContrato?.value || 0)}
                        </Text>
                      </TableCell>
                      <TableCell className="py-3">
                        {rollup.montoAfianzado > 0 || rollup.primaVigente > 0 ? (
                          <div className="text-xs">
                            <div className="text-zinc-700 font-semibold tabular-nums">
                              {fmtMXN(rollup.montoAfianzado)}
                            </div>
                            <div className="text-zinc-500 tabular-nums">
                              Prima: {fmtMXN(rollup.primaVigente)}
                            </div>
                          </div>
                        ) : (
                          <Text className="text-sm text-zinc-400">—</Text>
                        )}
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="max-w-xs">
                          <Text
                            className="text-sm font-semibold text-zinc-900 truncate"
                            title={contrato.tituloContrato?.value || 'Sin título'}
                          >
                            {contrato.tituloContrato?.value || 'Sin título'}
                          </Text>
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <Text className="text-sm text-zinc-900">
                          {contrato.beneficiario?.value || contrato.contratante?.value || 'N/A'}
                        </Text>
                      </TableCell>
                    </TableRow>

                    {expanded && (
                      <TableRow className="bg-azul-surelia/5">
                        {/* Spacer over the frozen columns. The panel is sticky,
                            and at the end of the scroll its containing block
                            shoves it back over the frozen strip — this opaque
                            z-10 cell is what it slides UNDER, instead of crossing
                            the freeze divider. Tint = bg-azul-surelia/5 flattened
                            over white. */}
                        <TableCell colSpan={2} style={{ left: 0 }} className="sticky z-10 bg-[#F4F6F7] p-0" />
                        <TableCell colSpan={7} className="py-3 px-6">
                          {/* Same drill-down the convenio tabs render: every
                              fianza emitida plus a card per requisito still por
                              emitir. */}
                          <ContratoFianzasPanel
                            stats={rollup}
                            contratoId={contrato.id}
                            onFianzaClick={(_contratoId, fianza) => openFianza(contrato, fianza)}
                            canUpload={false}
                            stickyOffset={frozen.total}
                          />
                          {rollup.documentos > 0 && (
                            <div className="sticky max-w-4xl mt-2" style={{ left: frozen.total }}>
                              <Text className="text-xs text-zinc-500">
                                {rollup.documentos} {rollup.documentos === 1 ? 'documento adjunto' : 'documentos adjuntos'}
                              </Text>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {paginatedContratos.length === 0 && (
                <TableRow>
                  <TableCell colSpan="9" className="py-8 text-center text-zinc-500">
                    No se encontraron contratos
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
              pinned to the boundary instead of riding along with the cells. */}
          {frozen.scrolled && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
              style={{ left: frozen.dividerLeft, bottom: frozen.dividerBottom }}
            />
          )}
        </div>

        {/* Pagination Controls (footer of the table card so it stays above the fold) */}
        {filteredContratos.length > 0 && (
        <div className="border-t border-zinc-200 px-4 py-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-zinc-600">
              Mostrando {startIndex + 1} - {Math.min(endIndex, filteredContratos.length)} de {filteredContratos.length} contratos
            </div>

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Anterior
              </Button>

              <div className="flex items-center gap-1">
                {(() => {
                  const pageNumbers = [];
                  const maxVisiblePages = 5;

                  const totalPagesLocal = Math.max(1, Math.ceil(filteredContratos.length / itemsPerPage));

                  if (totalPagesLocal <= maxVisiblePages) {
                    for (let i = 1; i <= totalPagesLocal; i++) {
                      pageNumbers.push(i);
                    }
                  } else {
                    pageNumbers.push(1);

                    if (currentPage <= 2) {
                      for (let i = 2; i <= Math.min(3, totalPagesLocal - 1); i++) {
                        pageNumbers.push(i);
                      }
                      if (totalPagesLocal > 3) pageNumbers.push('...');
                    } else if (currentPage >= totalPagesLocal - 1) {
                      if (totalPagesLocal > 3) pageNumbers.push('...');
                      for (let i = Math.max(totalPagesLocal - 2, 2); i <= totalPagesLocal - 1; i++) {
                        pageNumbers.push(i);
                      }
                    } else {
                      pageNumbers.push('...');
                      pageNumbers.push(currentPage);
                      pageNumbers.push('...');
                    }

                    if (totalPagesLocal > 1) pageNumbers.push(totalPagesLocal);
                  }

                  return pageNumbers.map((page, idx) => (
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-zinc-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        color={currentPage === page ? 'surelia' : 'white'}
                        className="text-sm px-3 py-2 min-w-[40px]"
                      >
                        {page}
                      </Button>
                    )
                  ));
                })()}
              </div>

              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                outline
                color="surelia"
                className="text-sm px-3 py-2"
              >
                Siguiente
              </Button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* ContratoView Modal */}
      {selectedContrato && (
        <ContratoView
          isOpen={isContratoViewOpen}
          onClose={handleContratoViewClose}
          contrato={selectedContrato}
          clientData={clientsData[selectedContrato.clientId]}
          onUpdate={() => {}}
          convenio={selectedContrato.convenioId ? conveniosById[selectedContrato.convenioId] : null}
          beneficiariosById={selectedContrato.convenioId
            ? Object.fromEntries((conveniosById[selectedContrato.convenioId]?.beneficiarios || []).map(b => [b.id, b]))
            : null}
        />
      )}

      {/* FianzaView Modal (fianza abierta desde el detalle expandido) */}
      {selectedFianza && (
        <FianzaView
          isOpen={isFianzaViewOpen}
          onClose={() => {
            setIsFianzaViewOpen(false);
            setSelectedFianza(null);
          }}
          fianza={selectedFianza}
          clientData={clientsData[selectedFianza.clientId]}
          contratoData={contractsList.find(c => c.id === selectedFianza.contratoId)}
          onUpdate={() => {}}
        />
      )}

      {/* Assignment Dialog */}
      <Dialog
        open={isAssignmentDialogOpen}
        onClose={() => setIsAssignmentDialogOpen(false)}
        size="lg"
      >
        <DialogTitle>Responsables del Contrato</DialogTitle>
        <DialogBody>
          {selectedContratoForAssignment && (
            <UserAssignmentSelector
              assignedUsers={selectedContratoForAssignment.assignedUsers || []}
              lockedUsers={clientsData[selectedContratoForAssignment.clientId]?.assignedUsers || []}
              onSave={async (userIds) => {
                const oldUsers = selectedContratoForAssignment.assignedUsers || [];
                const { description, changes, hasChanges } = computeUserAssignmentChanges(
                  oldUsers,
                  userIds,
                  getUserName
                );

                await updateContractAssignedUsers(selectedContratoForAssignment.id, userIds);

                if (hasChanges) {
                  try {
                    const clientData = clientsData[selectedContratoForAssignment.clientId];
                    const contratoTitle = selectedContratoForAssignment.tituloContrato?.value || 'Sin título';
                    const contratoDescription = `${description} en contrato "${contratoTitle}"`;
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'contracts',
                      documentId: selectedContratoForAssignment.id,
                      changes,
                      metadata: {
                        clientId: selectedContratoForAssignment.clientId,
                        clientName: clientData?.nombreEmpresa || 'Sin nombre',
                        contratoId: selectedContratoForAssignment.id,
                        description: contratoDescription,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log contract assignment to bitacora:', logError);
                  }
                }

                setIsAssignmentDialogOpen(false);
                setSelectedContratoForAssignment(null);
              }}
              onCancel={() => {
                setIsAssignmentDialogOpen(false);
                setSelectedContratoForAssignment(null);
              }}
            />
          )}
        </DialogBody>
      </Dialog>
    </div>
  );
};

export default Contratos;
